// ============================================================
// clientInfoRegistry.tsx
// THE CLIENT-INFO SECTION REGISTRY — one row per card on the Client
// Details tab (approved mockup casevo-client-details-view-edit.html,
// Option C). Each section declares:
//   key / title / sub / icon — the card header
//   fields                    — the person fields it owns (drives the
//                               Cancel snapshot/revert, the per-section
//                               validation filter and the collab
//                               broadcast keys)
//   View                      — read-only render (labelled values)
//   Edit                      — the inputs
// ClientInfoSections renders the cards from this table for BOTH modes, so
// the same blocks drop onto the case page later: the client page passes
// the live Client (fieldKeyPrefix 'client'); the case page passes the case
// snapshot (currentCase.clientDetails, prefix 'clientDetails').
// ============================================================
import React, { useState } from 'react'
import PhoneLink from '../shared/PhoneLink'
import FormField from '../shared/FormField'
import FieldError from '../shared/FieldError'
import AddressLookup from '../shared/AddressLookup'
import DateInput from '../shared/DateInput'
import { useCasevoStore } from '../../store/useCasevoStore'
import { dobError, dobWarning, serverDateErrorFor } from '../../utils/caseDateRules'
import { londonToday, isRealIsoDate, ukDate } from '../../utils/civilDate'
import { mainContactLabel } from '../../utils/mainContactLabel'
import { NO_EMAIL_LABEL, NO_EMAIL_ON_FILE, hasNoEmail } from '../../utils/noEmail'
import { caseScope, clientScope, useFieldRefusal } from '../../hooks/fieldRefusals'

// ── The person shape both Client and Case.clientDetails satisfy ──
export interface ClientInfoPerson {
  title: string | null
  forename: string
  surname: string
  addressLine1: string
  addressLine2: string
  addressTown: string
  addressCounty: string
  addressPostcode: string
  dateOfBirth: string | null
  hasNiNumber: boolean | null
  niNumber: string
  mainContactNumber: string
  emailAddress: string
  // B3 — false = "Client has no email address" (utils/noEmail).
  hasEmailAddress?: boolean | null
  employmentStatus: string | null
  occupation: string
  gpName?: string
  gpAddressLine1?: string
  gpAddressLine2?: string
  gpAddressTown?: string
  gpAddressCounty?: string
  gpAddressPostcode?: string
  gpPhone?: string
  hospitalName?: string
  hospitalAddressLine1?: string
  hospitalAddressLine2?: string
  hospitalAddressTown?: string
  hospitalAddressCounty?: string
  hospitalAddressPostcode?: string
  hospitalPhone?: string
  // Case-only fields (the case page's "On this case" card — clientDetails
  // describes the incident here, not the person). Absent on a live Client.
  timeOffWork?: boolean | null
  daysOffWork?: number | null
  medicalAttention?: string[]
}
export type PersonField = keyof ClientInfoPerson

// What a section's View/Edit receives from ClientInfoSections.
export interface SectionCtx {
  person: ClientInfoPerson
  set: (changes: Partial<ClientInfoPerson>) => void
  // Presence/live-sync fieldKey for a person field ('client.forename' …).
  fk: (field: string) => string
  // Programmatic multi-field updates (address lookup / clear) don't fire
  // focusout → broadcast them explicitly (same as the old form did).
  broadcast: (fields: PersonField[]) => void
  err: (field: PersonField) => string | undefined
  required: (field: PersonField) => boolean
  // Open this section for editing (the "Add" link on an empty value).
  onAdd?: () => void
  idPrefix: string
  // Case page only: per-field "out of date vs the live client" marker
  // (FormField outdated / outdatedLiveValue). Undefined on the client page.
  outdated?: (field: PersonField) => { outdated: boolean; outdatedLiveValue?: string } | undefined
  // (B2c) Case page only: PiMatter.protectedParty — the main contact is then
  // the litigation friend (mainContactLabel).
  protectedParty?: boolean
}

export type ClientInfoSectionKey = 'personal' | 'contact' | 'address' | 'identity' | 'onCase' | 'gp' | 'hospital'
export interface ClientInfoSectionDef {
  key: ClientInfoSectionKey
  title: string
  sub: string
  icon: React.ReactNode
  fields: PersonField[]
  // Case-page-only card (excluded unless a `sections` list asks for it).
  caseOnly?: boolean
  full?: boolean
  // Fields whose editors show in this card's header presence (defaults to
  // `fields`). onCase snapshots the GP/Hospital fields for Cancel, but a
  // colleague editing those belongs to the GP/Hospital cards, not this one.
  presenceFields?: PersonField[]
  View: React.FC<{ ctx: SectionCtx }>
  Edit: React.FC<{ ctx: SectionCtx }>
}

// ── icons (Feather, currentColor — design-language §6) ──
const Svg: React.FC<{ size?: number; children: React.ReactNode; sw?: number }> = ({ size = 17, children, sw = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0">{children}</svg>
)
export const IcUser = (s?: number): React.ReactNode => <Svg size={s}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></Svg>
export const IcPhone = (s?: number): React.ReactNode => <Svg size={s}><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" /></Svg>
export const IcPin = (s?: number): React.ReactNode => <Svg size={s}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></Svg>
export const IcId = (s?: number): React.ReactNode => <Svg size={s}><rect x="2" y="5" width="20" height="14" rx="2" /><circle cx="8" cy="12" r="2.5" /><path d="M14 10h5M14 14h3" /></Svg>
export const IcSteth = (s?: number): React.ReactNode => <Svg size={s}><path d="M5 3v6a5 5 0 0 0 10 0V3" /><path d="M10 14v2a5 5 0 0 0 10 0v-2" /><circle cx="20" cy="12" r="2" /></Svg>
export const IcHosp = (s?: number): React.ReactNode => <Svg size={s}><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M12 8v8M8 12h8" /></Svg>
export const IcMail = (s?: number): React.ReactNode => <Svg size={s}><path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" /><path d="M22 6l-10 7L2 6" /></Svg>
export const IcCal = (s?: number): React.ReactNode => <Svg size={s}><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></Svg>
export const IcEdit = (s?: number): React.ReactNode => <Svg size={s}><path d="M4 20h4L19 9l-4-4L4 16v4z" /></Svg>
export const IcCheck = (s?: number): React.ReactNode => <Svg size={s} sw={3}><path d="M20 6 9 17l-5-5" /></Svg>
const IcCopy = (s?: number): React.ReactNode => <Svg size={s}><rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" /></Svg>
const IcWork = (s?: number): React.ReactNode => <Svg size={s}><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></Svg>

// ── shared classes (mockup .kv / .inp / .seg / .pill …, exact values) ──
const DL = 'grid grid-cols-2 gap-x-[18px] gap-y-3 m-0'
const DL1 = 'grid grid-cols-1 gap-y-3 m-0'
const DT = 'text-[11px] font-bold tracking-[0.1em] uppercase text-casevo-text-soft mb-[3px]'
const DD = 'group/kv m-0 text-[14.5px] text-casevo-forest leading-[1.45] [overflow-wrap:anywhere] flex items-start gap-1.5'
const BIG = 'text-[17px] font-semibold tracking-[-0.005em]'
const MONO = 'font-mono text-[13.5px] tracking-[0.04em]'
const LINK = 'text-casevo-forest no-underline border-b border-casevo-border-soft hover:text-casevo-brand hover:border-casevo-mid focus:outline-none casevo-focus rounded-[2px]'
const PILL = 'inline-flex items-center gap-[5px] text-[12px] font-semibold px-[9px] py-[2px] rounded-full bg-casevo-mint text-casevo-brand dark:text-[#a8e6c5]'
const PILL_NEUTRAL = 'inline-flex items-center gap-[5px] text-[12px] font-semibold px-[9px] py-[2px] rounded-full bg-casevo-border-soft text-casevo-text-soft'
export const INP =
  'w-full h-[38px] rounded-[9px] border bg-casevo-input text-casevo-forest px-[11px] text-[14px] outline-none ' +
  'transition-[border-color,box-shadow,background-color] focus:border-casevo-mid focus:bg-casevo-surface ' +
  'focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--casevo-mid)_20%,transparent)] placeholder:text-casevo-text-soft'
export const SELECT_BG =
  'appearance-none pr-[30px] bg-no-repeat [background-image:linear-gradient(45deg,transparent_50%,var(--casevo-mid)_50%),linear-gradient(135deg,var(--casevo-mid)_50%,transparent_50%)] ' +
  '[background-position:calc(100%-16px)_16px,calc(100%-11px)_16px] [background-size:5px_5px]'
export const FGRID = 'grid grid-cols-1 sm:grid-cols-2 gap-x-[14px] gap-y-3'
export const inpCls = (bad: boolean): string => `${INP} ${bad ? 'border-casevo-danger' : 'border-casevo-border'}`

// ── view helpers ──
const fmtDob = (iso: string): string => {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}
export const ageOf = (iso: string): number | null => {
  const b = new Date(`${iso.slice(0, 10)}T00:00:00`)
  if (Number.isNaN(b.getTime())) return null
  const n = new Date()
  let a = n.getFullYear() - b.getFullYear()
  if (n.getMonth() < b.getMonth() || (n.getMonth() === b.getMonth() && n.getDate() < b.getDate())) a--
  return a
}
export const formatDob = fmtDob
export { MAIN_CONTACT_LABEL, MAIN_CONTACT_LABEL_FRIEND, mainContactLabel } from '../../utils/mainContactLabel'
const EMP_LABEL: Record<string, string> = { employed: 'Employed', 'self-employed': 'Self employed', unemployed: 'Unemployed' }

const CopyBtn: React.FC<{ value: string; label: string }> = ({ value, label }) => {
  const [done, setDone] = useState(false)
  return (
    <button
      type="button"
      title={done ? 'Copied' : `Copy ${label}`}
      aria-label={`Copy ${label}`}
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => { setDone(true); window.setTimeout(() => setDone(false), 1200) }, () => {})
      }}
      className="w-[30px] h-[30px] -mt-1 shrink-0 rounded-[8px] grid place-items-center text-casevo-text-soft hover:bg-casevo-mint hover:text-casevo-brand opacity-0 group-hover/kv:opacity-100 focus-visible:opacity-100 transition-opacity focus:outline-none casevo-focus"
    >
      {done ? IcCheck(13) : IcCopy(14)}
    </button>
  )
}
const Empty: React.FC<{ ctx: SectionCtx }> = ({ ctx }) => (
  <span className="text-casevo-text-soft italic text-[13.5px]">
    Not recorded
    {ctx.onAdd && (
      <button type="button" onClick={ctx.onAdd} className="not-italic ml-1.5 text-casevo-brand dark:text-casevo-mid font-semibold text-[12.5px] focus:outline-none casevo-focus rounded-[3px]">Add</button>
    )}
  </span>
)
const Row: React.FC<{ label: string; full?: boolean; children: React.ReactNode }> = ({ label, full, children }) => (
  <div className={`min-w-0 ${full ? 'col-span-full' : ''}`}>
    <dt className={DT}>{label}</dt>
    <dd className={DD}>{children}</dd>
  </div>
)
const addrLines = (p: ClientInfoPerson, prefix: 'address' | 'gpAddress' | 'hospitalAddress'): string[] =>
  [p[`${prefix}Line1` as PersonField], p[`${prefix}Line2` as PersonField], p[`${prefix}Town` as PersonField], p[`${prefix}County` as PersonField]]
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .filter(Boolean)

// ── edit helpers ──
// (Audit fix 1, M6) "Client has no email address". Ticking clears the email
// (the server does too), but the address is remembered for this edit:
// unticking it again before leaving the edit puts it back. (Cancel restores
// everything, as for any field.)
const NoEmailToggle: React.FC<{ ctx: SectionCtx }> = ({ ctx }) => {
  const remembered = React.useRef<string>('')
  return (
    <label className="inline-flex items-center gap-[9px] text-[13.5px] text-casevo-forest cursor-pointer select-none -mt-1">
      <input type="checkbox" className="h-[17px] w-[17px] accent-casevo-brand shrink-0" checked={hasNoEmail(ctx.person)}
        onChange={(e) => {
          if (e.target.checked) {
            if (ctx.person.emailAddress.trim()) remembered.current = ctx.person.emailAddress
            ctx.set({ hasEmailAddress: false, emailAddress: '' })
          } else {
            ctx.set(remembered.current ? { hasEmailAddress: null, emailAddress: remembered.current } : { hasEmailAddress: null })
            remembered.current = ''
          }
        }} />
      {NO_EMAIL_LABEL}
      {hasNoEmail(ctx.person) && remembered.current && <span className="text-[12px] text-casevo-text-soft">(untick to put {remembered.current} back)</span>}
    </label>
  )
}
const TextField: React.FC<{
  ctx: SectionCtx; f: PersonField; label: string; type?: string; placeholder?: string; transform?: (v: string) => string; inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
  // B3 — e.g. the email when "Client has no email address" is ticked (also not required then).
  disabled?: boolean
}> = ({ ctx, f, label, type = 'text', placeholder, transform, inputMode, disabled }) => {
  const id = `${ctx.idPrefix}-${f}`
  const err = ctx.err(f)
  const raw = ctx.person[f]
  return (
    <FormField variant="info" label={label} fieldKey={ctx.fk(f)} htmlFor={id} required={ctx.required(f) && !disabled} {...ctx.outdated?.(f)}>
      <input
        id={id}
        type={type}
        disabled={disabled}
        inputMode={inputMode}
        value={typeof raw === 'string' ? raw : ''}
        placeholder={placeholder}
        aria-invalid={err ? true : undefined}
        onChange={(e) => ctx.set({ [f]: transform ? transform(e.target.value) : e.target.value } as Partial<ClientInfoPerson>)}
        className={`${inpCls(!!err)}${disabled ? ' opacity-60 cursor-not-allowed' : ''}`}
      />
      <FieldError message={err} />
    </FormField>
  )
}

type AddrPrefix = 'address' | 'gpAddress' | 'hospitalAddress'
const addrFields = (p: AddrPrefix): PersonField[] =>
  (['Line1', 'Line2', 'Town', 'County', 'Postcode'] as const).map((s) => `${p}${s}` as PersonField)

const SEARCH_SLUG: Record<AddrPrefix, string> = { address: 'address-search', gpAddress: 'gp-address-search', hospitalAddress: 'hospital-address-search' }
const AddressEdit: React.FC<{ ctx: SectionCtx; prefix: AddrPrefix; upperPostcode?: boolean }> = ({ ctx, prefix, upperPostcode }) => {
  const p = ctx.person
  const g = (s: string): string => {
    const v = p[`${prefix}${s}` as PersonField]
    return typeof v === 'string' ? v : ''
  }
  const has = ['Line1', 'Line2', 'Town', 'County', 'Postcode'].some((s) => g(s))
  const lookupId = `${ctx.idPrefix}-${prefix}-search`
  const write = (a: { line1: string; line2: string; town: string; county: string; postcode: string }): void => {
    ctx.set({
      [`${prefix}Line1`]: a.line1, [`${prefix}Line2`]: a.line2, [`${prefix}Town`]: a.town,
      [`${prefix}County`]: a.county, [`${prefix}Postcode`]: a.postcode,
    } as Partial<ClientInfoPerson>)
    ctx.broadcast(addrFields(prefix))
  }
  return (
    <>
      {/* Search box presence key = the old form's ('…address-search',
          '…gp-address-search', '…hospital-address-search'); presence only —
          the field-edit engines ignore address-search keys. */}
      <FormField variant="info" label="Search address" fieldKey={ctx.fk(SEARCH_SLUG[prefix])} htmlFor={lookupId}>
        <AddressLookup
          variant="info"
          inputId={lookupId}
          value={has ? { line1: g('Line1'), line2: g('Line2'), town: g('Town'), county: g('County'), postcode: g('Postcode') } : null}
          onSelect={(addr) => write(addr)}
          onClear={() => write({ line1: '', line2: '', town: '', county: '', postcode: '' })}
        />
      </FormField>
      <div className="mt-3 pt-3 border-t border-dashed border-casevo-border-soft">
        <div className="text-[11px] font-bold tracking-[0.1em] uppercase text-casevo-text-soft mb-2">Address</div>
        <div className={FGRID}>
          <div className="sm:col-span-2"><TextField ctx={ctx} f={`${prefix}Line1` as PersonField} label="Address line 1" /></div>
          <div className="sm:col-span-2"><TextField ctx={ctx} f={`${prefix}Line2` as PersonField} label="Address line 2" /></div>
          <TextField ctx={ctx} f={`${prefix}Town` as PersonField} label="Town" />
          <TextField ctx={ctx} f={`${prefix}County` as PersonField} label="County" />
          <TextField ctx={ctx} f={`${prefix}Postcode` as PersonField} label="Postcode" placeholder="LS6 3AB" transform={upperPostcode ? (v) => v.toUpperCase() : undefined} />
        </div>
      </div>
    </>
  )
}

// (B4 follow-ups) The SAME address block as the client's (AddressEdit: the
// postcode search, then the five fields with the same labels, layout and
// postcode capitals) for an address that isn't on the client record — the
// litigation friend's (LitigationFriendCard). Plain value / onChange; no
// client-record presence or refusals. `locked` = read-only fields, no search
// ("Same address as the client").
export type AddressValue = { line1: string; line2: string; town: string; county: string; postcode: string }
export const EMPTY_ADDRESS: AddressValue = { line1: '', line2: '', town: '', county: '', postcode: '' }
export const AddressFieldsEdit: React.FC<{
  idPrefix: string
  value: AddressValue
  onChange: (a: AddressValue) => void
  errorOf?: (k: keyof AddressValue) => string | undefined
  locked?: boolean
  // Above the fields (e.g. the old one-box text that couldn't be split).
  note?: React.ReactNode
}> = ({ idPrefix, value, onChange, errorOf, locked = false, note }) => {
  const has = Object.values(value).some((v) => v.trim())
  const lookupId = `${idPrefix}-address-search`
  const input = (k: keyof AddressValue, label: string, extra: { placeholder?: string; upper?: boolean } = {}): React.ReactNode => {
    const id = `${idPrefix}-${k}`
    const err = errorOf?.(k)
    return (
      <FormField variant="info" label={label} htmlFor={id}>
        <input id={id} value={value[k]} readOnly={locked} placeholder={extra.placeholder} aria-invalid={err ? true : undefined}
          onChange={(e) => onChange({ ...value, [k]: extra.upper ? e.target.value.toUpperCase() : e.target.value })}
          className={`${inpCls(!!err)}${locked ? ' opacity-80' : ''}`} />
        <FieldError message={err} />
      </FormField>
    )
  }
  return (
    <>
      {!locked && (
        <FormField variant="info" label="Search address" htmlFor={lookupId}>
          <AddressLookup variant="info" inputId={lookupId} value={has ? value : null}
            onSelect={(a) => onChange({ line1: a.line1 ?? '', line2: a.line2 ?? '', town: a.town ?? '', county: a.county ?? '', postcode: a.postcode ?? '' })}
            onClear={() => onChange(EMPTY_ADDRESS)} />
        </FormField>
      )}
      <div className={`${locked ? '' : 'mt-3 '}pt-3 border-t border-dashed border-casevo-border-soft`}>
        <div className="text-[11px] font-bold tracking-[0.1em] uppercase text-casevo-text-soft mb-2">Address</div>
        {note}
        <div className={FGRID}>
          <div className="sm:col-span-2">{input('line1', 'Address line 1')}</div>
          <div className="sm:col-span-2">{input('line2', 'Address line 2')}</div>
          {input('town', 'Town')}
          {input('county', 'County')}
          {input('postcode', 'Postcode', { placeholder: 'LS6 3AB', upper: true })}
        </div>
      </div>
    </>
  )
}

// GP / Hospital share one anatomy.
const OrgView: React.FC<{ ctx: SectionCtx; prefix: 'gp' | 'hospital'; label: string }> = ({ ctx, prefix, label }) => {
  const p = ctx.person
  const name = (p[`${prefix}Name` as PersonField] as string | undefined) ?? ''
  const phone = (p[`${prefix}Phone` as PersonField] as string | undefined) ?? ''
  const pc = (p[`${prefix}AddressPostcode` as PersonField] as string | undefined) ?? ''
  const lines = addrLines(p, `${prefix}Address` as AddrPrefix).concat(pc.trim() ? [pc.trim()] : [])
  return (
    <div className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] gap-x-[18px] gap-y-3">
      <dl className={DL1}>
        <Row label={label}>{name.trim() ? <span className="min-w-0 font-semibold">{name}</span> : <Empty ctx={ctx} />}</Row>
        <Row label="Address">{lines.length ? <span className="min-w-0 whitespace-pre-line">{lines.join('\n')}</span> : <Empty ctx={ctx} />}</Row>
      </dl>
      <dl className={DL1}>
        <Row label="Phone">
          {phone.trim()
            ? <><span className="min-w-0"><PhoneLink value={phone} className={LINK} /></span><CopyBtn value={phone} label="phone" /></>
            : <Empty ctx={ctx} />}
        </Row>
      </dl>
    </div>
  )
}
const OrgEdit: React.FC<{ ctx: SectionCtx; prefix: 'gp' | 'hospital'; nameLabel: string }> = ({ ctx, prefix, nameLabel }) => (
  <>
    <TextField ctx={ctx} f={`${prefix}Name` as PersonField} label={nameLabel} />
    <div className="mt-3"><AddressEdit ctx={ctx} prefix={`${prefix}Address` as AddrPrefix} /></div>
    <div className={`${FGRID} mt-3`}>
      <TextField ctx={ctx} f={`${prefix}Phone` as PersonField} label="Phone" type="tel" placeholder="07700 900000" />
    </div>
  </>
)

// Empty-string GP / Hospital groups — spread into the medical-attention
// write whenever a ticked box leaves view (same as the old case form).
const EMPTY_GP_DETAILS: Partial<ClientInfoPerson> = {
  gpName: '', gpAddressLine1: '', gpAddressLine2: '', gpAddressTown: '', gpAddressCounty: '', gpAddressPostcode: '', gpPhone: '',
}
const EMPTY_HOSPITAL_DETAILS: Partial<ClientInfoPerson> = {
  hospitalName: '', hospitalAddressLine1: '', hospitalAddressLine2: '', hospitalAddressTown: '', hospitalAddressCounty: '', hospitalAddressPostcode: '', hospitalPhone: '',
}

// ══════════════════════════════════════════════════════════════════
// THE REGISTRY
// ══════════════════════════════════════════════════════════════════
// Date of birth (client profile + case snapshot). DATE HARDENING: a
// half-typed date is never saved (DateInput); a future DOB is refused on the
// spot (the server 400s the same) and isn't autosaved; the server's refusal
// shows under the field; amber when under 18 today or over 110.
// On the CASE page a DOB after this case's accident is refused on the spot too
// (the server 400s it). The client profile doesn't hold its cases' accident
// dates, so there the server's refusal (field dateOfBirth) shows under the
// field and the saved DOB comes back (hooks/fieldRefusals).
const DobField: React.FC<{ value: string | null; validationError?: string; onSave: (v: string | null) => void; onCase: boolean }> = ({ value, validationError, onSave, onCase }) => {
  const [block, setBlock] = useState<string | null>(null)
  const saveError = useCasevoStore((st) => st.saveError)
  const caseId = useCasevoStore((st) => st.currentCase?.id ?? null)
  const clientId = useCasevoStore((st) => st.selectedClient?.id ?? null)
  const field = onCase ? 'clientDateOfBirth' : 'dateOfBirth'
  const refused = useFieldRefusal(onCase ? (caseId ? caseScope(caseId) : null) : (clientId ? clientScope(clientId) : null), field)
  const serverErr = refused ?? serverDateErrorFor(saveError, field)
  const warn = dobWarning(value, londonToday())
  const err = block ?? serverErr ?? validationError
  return (
    <>
      <DateInput
        ariaLabel="Date of birth"
        value={value}
        onChange={(v) => {
          let b = dobError(v, londonToday())
          const accident = onCase ? useCasevoStore.getState().currentCase?.accidentDetails?.accidentDate : null
          if (!b && isRealIsoDate(v) && isRealIsoDate(accident) && v > accident) b = `Date of birth is after the accident date (${ukDate(accident)}).`
          setBlock(b)
          if (!b) onSave(v)
        }}
        fieldClassName={`h-[38px] !rounded-[9px] !bg-casevo-input focus:!bg-casevo-surface ${err ? 'border-casevo-danger' : 'border-casevo-border'}`}
      />
      <FieldError message={err ?? undefined} />
      {!err && warn && <span className="block text-[12px] text-casevo-warn mt-1" role="status">{warn}</span>}
    </>
  )
}

export const CLIENT_INFO_SECTIONS: ClientInfoSectionDef[] = [
  {
    key: 'personal', title: 'Personal', sub: 'Name and date of birth', icon: IcUser(17),
    fields: ['title', 'forename', 'surname', 'dateOfBirth'],
    View: ({ ctx }) => {
      const p = ctx.person
      const name = [p.title, p.forename, p.surname].map((s) => (s ?? '').trim()).filter(Boolean).join(' ')
      const age = p.dateOfBirth ? ageOf(p.dateOfBirth) : null
      return (
        <dl className={DL}>
          <Row label="Name" full>{name ? <span className={`min-w-0 ${BIG}`}>{name}</span> : <Empty ctx={ctx} />}</Row>
          <Row label="Date of birth">{p.dateOfBirth ? <span className="min-w-0 tabular-nums">{fmtDob(p.dateOfBirth)}</span> : <Empty ctx={ctx} />}</Row>
          <Row label="Age">{age !== null ? <span className="min-w-0 tabular-nums">{age}</span> : <span className="text-casevo-text-soft">—</span>}</Row>
        </dl>
      )
    },
    Edit: ({ ctx }) => {
      const p = ctx.person
      const id = `${ctx.idPrefix}-title`
      const dobErr = ctx.err('dateOfBirth')
      return (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-[110px_minmax(0,1fr)_minmax(0,1fr)] gap-x-[14px] gap-y-3">
            <FormField variant="info" label="Title" fieldKey={ctx.fk('title')} htmlFor={id} {...ctx.outdated?.('title')}>
              <select id={id} value={p.title ?? ''} onChange={(e) => ctx.set({ title: e.target.value || null })} className={`${inpCls(false)} ${SELECT_BG}`}>
                <option value="">Select</option>
                <option>Mr</option><option>Mrs</option><option>Miss</option><option>Ms</option><option>Dr</option><option>Other</option>
              </select>
            </FormField>
            <TextField ctx={ctx} f="forename" label="Forename" />
            <TextField ctx={ctx} f="surname" label="Surname" />
          </div>
          <div className={`${FGRID} mt-3`}>
            <FormField variant="info" label="Date of birth" fieldKey={ctx.fk('dateOfBirth')} required={ctx.required('dateOfBirth')} {...ctx.outdated?.('dateOfBirth')}>
              <DobField value={p.dateOfBirth} validationError={dobErr} onSave={(value) => ctx.set({ dateOfBirth: value })} onCase={ctx.fk('dateOfBirth').startsWith('clientDetails.')} />
            </FormField>
          </div>
        </>
      )
    },
  },
  {
    key: 'contact', title: 'Contact', sub: 'How to reach them', icon: IcPhone(17),
    fields: ['mainContactNumber', 'emailAddress', 'hasEmailAddress'],
    View: ({ ctx }) => {
      const p = ctx.person
      return (
        <dl className={DL1}>
          <Row label={mainContactLabel(p.dateOfBirth, ctx.protectedParty)}>
            {p.mainContactNumber.trim()
              ? <><span className="min-w-0"><PhoneLink value={p.mainContactNumber} className={`${LINK} ${BIG}`} plainClassName={`${BIG} tabular-nums`} /></span><CopyBtn value={p.mainContactNumber} label="number" /></>
              : <Empty ctx={ctx} />}
          </Row>
          <Row label="Email address">
            {hasNoEmail(p)
              ? <span className="text-casevo-text-soft">{NO_EMAIL_ON_FILE}</span>
              : p.emailAddress.trim()
                ? <><span className="min-w-0"><a className={LINK} href={`mailto:${p.emailAddress}`}>{p.emailAddress}</a></span><CopyBtn value={p.emailAddress} label="email" /></>
                : <Empty ctx={ctx} />}
          </Row>
        </dl>
      )
    },
    Edit: ({ ctx }) => (
      <div className="grid grid-cols-1 gap-y-3">
        <TextField ctx={ctx} f="mainContactNumber" label={mainContactLabel(ctx.person.dateOfBirth, ctx.protectedParty)} type="tel" placeholder="07700 900000" />
        <TextField ctx={ctx} f="emailAddress" label="Email address" type="email" placeholder="name@example.com" disabled={hasNoEmail(ctx.person)} />
        {/* B3 — no email address: not required; email features say so. */}
        <NoEmailToggle ctx={ctx} />
      </div>
    ),
  },
  {
    key: 'address', title: 'Home address', sub: 'Correspondence address', icon: IcPin(17),
    fields: addrFields('address'),
    View: ({ ctx }) => {
      const p = ctx.person
      const lines = addrLines(p, 'address')
      const pc = p.addressPostcode.trim()
      return (
        <dl className={DL1}>
          <Row label="Address">
            {lines.length
              ? <><span className="min-w-0 whitespace-pre-line">{lines.join('\n')}</span><CopyBtn value={lines.concat(pc ? [pc] : []).join(', ')} label="address" /></>
              : <Empty ctx={ctx} />}
          </Row>
          <Row label="Postcode">{pc ? <span className={`min-w-0 ${MONO}`}>{pc}</span> : <Empty ctx={ctx} />}</Row>
        </dl>
      )
    },
    Edit: ({ ctx }) => <AddressEdit ctx={ctx} prefix="address" upperPostcode />,
  },
  {
    key: 'identity', title: 'Identity & employment', sub: 'National Insurance and work', icon: IcId(17),
    fields: ['hasNiNumber', 'niNumber', 'employmentStatus', 'occupation'],
    View: ({ ctx }) => {
      const p = ctx.person
      const emp = p.employmentStatus ? EMP_LABEL[p.employmentStatus] ?? p.employmentStatus : null
      const working = p.employmentStatus === 'employed' || p.employmentStatus === 'self-employed'
      return (
        <dl className={DL}>
          <Row label="National Insurance number" full>
            {p.hasNiNumber === true && p.niNumber.trim()
              ? <><span className={`min-w-0 ${MONO}`}>{p.niNumber}</span><CopyBtn value={p.niNumber} label="NI number" /></>
              : p.hasNiNumber === false
                ? <span className={PILL_NEUTRAL}>No NI number</span>
                : <Empty ctx={ctx} />}
          </Row>
          <Row label="Employment">{emp ? <span className={PILL}>{IcWork(12)}{emp}</span> : <Empty ctx={ctx} />}</Row>
          {working && <Row label="Occupation">{p.occupation.trim() ? <span className="min-w-0">{p.occupation}</span> : <Empty ctx={ctx} />}</Row>}
        </dl>
      )
    },
    Edit: ({ ctx }) => {
      const p = ctx.person
      const empId = `${ctx.idPrefix}-employmentStatus`
      const working = p.employmentStatus === 'employed' || p.employmentStatus === 'self-employed'
      // Real radios (visually a segmented control) so the live-sync engine's
      // `change` listener still fires exactly as it did for the old radios.
      const seg = (on: boolean): string =>
        `relative inline-flex items-center px-4 text-[13.5px] border-r border-casevo-border last:border-r-0 cursor-pointer has-[:focus-visible]:shadow-[inset_0_0_0_2px_var(--casevo-focus)] ${on ? 'bg-casevo-primary text-white font-semibold' : 'text-casevo-mid hover:bg-casevo-mint/40'}`
      return (
        <div className={FGRID}>
          <div className="sm:col-span-2">
            <FormField variant="info" label="Does the client have a National Insurance number?" fieldKey={ctx.fk('hasNiNumber')} {...ctx.outdated?.('hasNiNumber')}>
              <div role="radiogroup" aria-label="Does the client have a National Insurance number?" className="inline-flex border border-casevo-border rounded-[9px] overflow-hidden bg-casevo-surface h-[38px]">
                <label className={seg(p.hasNiNumber === true)}>
                  <input type="radio" className="sr-only" name={`${ctx.idPrefix}-hasNi`} checked={p.hasNiNumber === true} onChange={() => ctx.set({ hasNiNumber: true })} />Yes
                </label>
                <label className={seg(p.hasNiNumber === false)}>
                  {/* Clears niNumber in the SAME update — mirrors the old form. */}
                  <input type="radio" className="sr-only" name={`${ctx.idPrefix}-hasNi`} checked={p.hasNiNumber === false} onChange={() => ctx.set({ hasNiNumber: false, niNumber: '' })} />No
                </label>
              </div>
            </FormField>
          </div>
          {p.hasNiNumber === true && (
            <>
              <TextField ctx={ctx} f="niNumber" label="NI number" placeholder="AB 12 34 56 C" />
              <div className="hidden sm:block" />
            </>
          )}
          <FormField variant="info" label="Employment details" fieldKey={ctx.fk('employmentStatus')} htmlFor={empId} {...ctx.outdated?.('employmentStatus')}>
            <select id={empId} value={p.employmentStatus ?? ''} onChange={(e) => ctx.set({ employmentStatus: e.target.value || null })} className={`${inpCls(false)} ${SELECT_BG}`}>
              <option value="">Select</option>
              <option value="employed">Employed</option>
              <option value="self-employed">Self Employed</option>
              <option value="unemployed">Unemployed</option>
            </select>
          </FormField>
          {working && <TextField ctx={ctx} f="occupation" label="Occupation" />}
        </div>
      )
    },
  },
  {
    // CASE PAGE ONLY — the incident fields that live on currentCase.clientDetails
    // (not the person; never copied back to the client). Mirrors the old
    // ClientDetailsSection exactly: "No" time off clears days in the SAME
    // write; medical attention is mutually exclusive No vs GP/Hospital and
    // clears the GP/Hospital fields in the SAME write when they leave view.
    // The GP/Hospital fields are in `fields` so Cancel restores them too.
    key: 'onCase', title: 'On this case', sub: 'Case-specific — not copied back to the client', icon: IcWork(17),
    caseOnly: true, full: true,
    presenceFields: ['timeOffWork', 'daysOffWork', 'medicalAttention'],
    fields: ['timeOffWork', 'daysOffWork', 'medicalAttention', 'gpName', ...addrFields('gpAddress'), 'gpPhone', 'hospitalName', ...addrFields('hospitalAddress'), 'hospitalPhone'],
    View: ({ ctx }) => {
      const p = ctx.person
      const ma = p.medicalAttention ?? []
      return (
        <dl className={DL}>
          <Row label="Time off work">
            {p.timeOffWork === true
              ? <span className="min-w-0">Yes{p.daysOffWork !== null && p.daysOffWork !== undefined ? <> · <b className="font-semibold tabular-nums">{p.daysOffWork}</b> day{p.daysOffWork === 1 ? '' : 's'}</> : ''}</span>
              : p.timeOffWork === false ? <span className={PILL_NEUTRAL}>No</span> : <Empty ctx={ctx} />}
          </Row>
          <Row label="Medical attention">
            {ma.length === 0 ? <Empty ctx={ctx} /> : (
              <span className="min-w-0 flex gap-1.5 flex-wrap">
                {ma.includes('no') && <span className={PILL_NEUTRAL}>No medical attention</span>}
                {ma.includes('gp') && <span className={PILL}>{IcSteth(12)}GP</span>}
                {ma.includes('hospital') && <span className={PILL}>{IcHosp(12)}Hospital</span>}
              </span>
            )}
          </Row>
        </dl>
      )
    },
    Edit: ({ ctx }) => {
      const p = ctx.person
      const ma = p.medicalAttention ?? []
      const daysId = `${ctx.idPrefix}-daysOffWork`
      const seg = (on: boolean): string =>
        `relative inline-flex items-center px-4 text-[13.5px] border-r border-casevo-border last:border-r-0 cursor-pointer has-[:focus-visible]:shadow-[inset_0_0_0_2px_var(--casevo-focus)] ${on ? 'bg-casevo-primary text-white font-semibold' : 'text-casevo-mid hover:bg-casevo-mint/40'}`
      const toggle = (option: 'no' | 'gp' | 'hospital', checked: boolean): void => {
        let updated: string[]
        let dependentClears: Partial<ClientInfoPerson> = {}
        if (!checked) {
          updated = ma.filter((m) => m !== option)
          if (option === 'gp') dependentClears = { ...EMPTY_GP_DETAILS }
          else if (option === 'hospital') dependentClears = { ...EMPTY_HOSPITAL_DETAILS }
        } else if (option === 'no') {
          updated = ['no']
          dependentClears = { ...EMPTY_GP_DETAILS, ...EMPTY_HOSPITAL_DETAILS }
        } else {
          updated = [...ma.filter((m) => m !== 'no' && m !== option), option]
        }
        // ONE write — medicalAttention + the dependent clears together.
        ctx.set({ medicalAttention: updated, ...dependentClears })
      }
      const chk = (option: 'no' | 'gp' | 'hospital', label: string): React.ReactNode => {
        const on = ma.includes(option)
        return (
          <label key={option} className={`inline-flex items-center gap-2 h-[38px] px-3 border rounded-[9px] text-[13.5px] font-medium cursor-pointer text-casevo-forest has-[:focus-visible]:shadow-[0_0_0_3px_color-mix(in_srgb,var(--casevo-mid)_25%,transparent)] ${on ? 'border-casevo-mid bg-casevo-mint' : 'border-casevo-border bg-casevo-surface'}`}>
            <input type="checkbox" className="sr-only" checked={on} onChange={(e) => toggle(option, e.target.checked)} />
            <span aria-hidden="true" className={`w-4 h-4 rounded-[4px] border-[1.5px] grid place-items-center ${on ? 'bg-casevo-primary border-casevo-primary text-white' : 'border-casevo-border'}`}>{on && IcCheck(11)}</span>
            {label}
          </label>
        )
      }
      return (
        <div className={FGRID}>
          <FormField variant="info" label="Time off work" fieldKey={ctx.fk('timeOffWork')}>
            <div role="radiogroup" aria-label="Time off work" className="inline-flex border border-casevo-border rounded-[9px] overflow-hidden bg-casevo-surface h-[38px]">
              <label className={seg(p.timeOffWork === true)}>
                <input type="radio" className="sr-only" name={`${ctx.idPrefix}-timeOff`} checked={p.timeOffWork === true} onChange={() => ctx.set({ timeOffWork: true })} />Yes
              </label>
              <label className={seg(p.timeOffWork === false)}>
                {/* Both fields in ONE write (two writes would clobber). */}
                <input type="radio" className="sr-only" name={`${ctx.idPrefix}-timeOff`} checked={p.timeOffWork === false} onChange={() => ctx.set({ timeOffWork: false, daysOffWork: null })} />No
              </label>
            </div>
          </FormField>
          {p.timeOffWork === true ? (
            <FormField variant="info" label="Number of days off" fieldKey={ctx.fk('daysOffWork')} htmlFor={daysId}>
              <input id={daysId} type="number" min={1} value={p.daysOffWork || ''} onChange={(e) => {
                // Clamp to a sane whole-number range — an out-of-range value was
                // rejected by the server and then blocked EVERY later case save.
                const n = e.target.value ? parseInt(e.target.value, 10) : NaN
                ctx.set({ daysOffWork: Number.isFinite(n) ? Math.min(Math.max(n, 0), 9999) : null })
              }} className={`${inpCls(false)} tabular-nums`} />
            </FormField>
          ) : <div className="hidden sm:block" />}
          <div className="sm:col-span-2">
            <FormField variant="info" label="Medical attention" fieldKey={ctx.fk('medicalAttention')}>
              <div className="flex gap-2 flex-wrap">
                {chk('no', 'No medical attention')}
                {chk('gp', 'GP')}
                {chk('hospital', 'Hospital')}
              </div>
            </FormField>
          </div>
        </div>
      )
    },
  },
  {
    key: 'gp', title: 'GP', sub: 'Registered practice', icon: IcSteth(17),
    fields: ['gpName', ...addrFields('gpAddress'), 'gpPhone'],
    View: ({ ctx }) => <OrgView ctx={ctx} prefix="gp" label="Practice" />,
    Edit: ({ ctx }) => <OrgEdit ctx={ctx} prefix="gp" nameLabel="GP name" />,
  },
  {
    key: 'hospital', title: 'Hospital', sub: 'Where they were treated', icon: IcHosp(17),
    fields: ['hospitalName', ...addrFields('hospitalAddress'), 'hospitalPhone'],
    View: ({ ctx }) => <OrgView ctx={ctx} prefix="hospital" label="Hospital" />,
    Edit: ({ ctx }) => <OrgEdit ctx={ctx} prefix="hospital" nameLabel="Hospital name" />,
  },
]
export const ALL_PERSON_FIELDS: PersonField[] = CLIENT_INFO_SECTIONS.flatMap((s) => s.fields)
